import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

let ffmpegInstance: FFmpeg | null = null;

/**
 * Inicializa FFmpeg WASM
 */
const loadFFmpeg = async (): Promise<FFmpeg> => {
  if (ffmpegInstance) {
    return ffmpegInstance;
  }

  const ffmpeg = new FFmpeg();
  
  ffmpeg.on('log', ({ message }) => {
    console.log('[FFmpeg]', message);
  });

  const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
  
  await ffmpeg.load({
    coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
    wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
  });

  ffmpegInstance = ffmpeg;
  return ffmpeg;
};

/**
 * Comprime un archivo de video usando FFmpeg.wasm
 * @param file - Archivo de video original
 * @param onProgress - Callback para reportar el progreso (0-100)
 * @returns Archivo de video comprimido
 */
export const compressVideo = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<File> => {
  try {
    console.log(`Iniciando compresión de video: ${file.name}`);
    console.log(`Tamaño original: ${(file.size / 1024 / 1024).toFixed(2)}MB`);

    const ffmpeg = await loadFFmpeg();

    // Configurar callback de progreso
    ffmpeg.on('progress', ({ progress }) => {
      const percentage = Math.round(progress * 100);
      console.log(`Progreso de compresión: ${percentage}%`);
      onProgress?.(percentage);
    });

    // Escribir archivo de entrada
    await ffmpeg.writeFile('input.mp4', await fetchFile(file));

    // Comprimir video con parámetros óptimos
    await ffmpeg.exec([
      '-i', 'input.mp4',
      '-vf', 'scale=-2:720',           // Escalar a 720p manteniendo aspecto
      '-c:v', 'libx264',               // Codec H.264
      '-crf', '28',                    // Factor de calidad (28 = buena compresión)
      '-preset', 'fast',               // Velocidad de compresión
      '-c:a', 'aac',                   // Audio AAC
      '-b:a', '128k',                  // Bitrate de audio 128kbps
      '-movflags', '+faststart',       // Optimizar para streaming web
      'output.mp4'
    ]);

    // Leer archivo comprimido
    const data = await ffmpeg.readFile('output.mp4');
    
    // Limpiar archivos temporales
    await ffmpeg.deleteFile('input.mp4');
    await ffmpeg.deleteFile('output.mp4');

    // Crear nuevo File con el video comprimido
    const compressedFile = new File(
      [new Uint8Array(data as Uint8Array)],
      file.name.replace(/\.[^/.]+$/, '.mp4'),
      { type: 'video/mp4' }
    );

    const compressedSize = compressedFile.size / 1024 / 1024;
    const originalSize = file.size / 1024 / 1024;
    const reduction = ((1 - compressedSize / originalSize) * 100).toFixed(1);

    console.log(`Tamaño comprimido: ${compressedSize.toFixed(2)}MB`);
    console.log(`Reducción: ${reduction}%`);

    return compressedFile;
  } catch (error) {
    console.error('Error al comprimir video:', error);
    throw new Error('No se pudo comprimir el video. Intenta con un archivo más pequeño.');
  }
};

/**
 * Verifica si el navegador soporta FFmpeg.wasm
 */
export const isFFmpegSupported = (): boolean => {
  return typeof SharedArrayBuffer !== 'undefined';
};
